import { ChevronUp, MessageCircle, CheckCircle2 } from 'lucide-react'
import type { FeedbackItem } from '../../lib/feedbackApi'

const TYPE_BADGE: Record<string, { bg: string; text: string; label: string }> = {
  bug: { bg: 'bg-red-500/10', text: 'text-red-600 dark:text-red-400', label: 'Bug' },
  feature: { bg: 'bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400', label: 'Feature' },
  improvement: { bg: 'bg-green-500/10', text: 'text-green-600 dark:text-green-400', label: 'Improvement' },
  integration: { bg: 'bg-purple-500/10', text: 'text-purple-600 dark:text-purple-400', label: 'Integration' },
}

interface KanbanCardProps {
  item: FeedbackItem
  isAdmin: boolean
  onCardClick: (id: number) => void
}

export function KanbanCard({ item, isAdmin, onCardClick }: KanbanCardProps) {
  const typeBadge = item.type ? TYPE_BADGE[item.type] : null
  const displayStatus = item.status === 'dismissed' ? (item.previous_status || 'backlog') : item.status
  const isDone = displayStatus === 'done'
  const isInProgress = displayStatus === 'in_progress'

  function handleDragStart(e: React.DragEvent) {
    e.dataTransfer.setData('text/plain', String(item.id))
    e.dataTransfer.effectAllowed = 'move'
    if (e.currentTarget instanceof HTMLElement) {
      e.dataTransfer.setDragImage(e.currentTarget, 20, 20)
    }
  }

  function handleClick(e: React.MouseEvent) {
    if ((e.currentTarget as HTMLElement).dataset.wasDragged === 'true') {
      (e.currentTarget as HTMLElement).dataset.wasDragged = 'false'
      return
    }
    onCardClick(item.id)
  }

  function handleDragEnd(e: React.DragEvent) {
    if (e.dataTransfer.dropEffect !== 'none') {
      (e.currentTarget as HTMLElement).dataset.wasDragged = 'true'
    }
  }

  return (
    <div
      onClick={handleClick}
      draggable={isAdmin}
      onDragStart={isAdmin ? handleDragStart : undefined}
      onDragEnd={isAdmin ? handleDragEnd : undefined}
      className={`border rounded-lg p-3 hover:shadow-md transition-all ${
        isDone
          ? 'bg-card/50 border-border/50 opacity-60'
          : isInProgress
            ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800/40 hover:border-amber-300 dark:hover:border-amber-700/50'
            : 'bg-card border-border hover:border-primary/30'
      } ${isAdmin ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}`}
    >
      {/* Title */}
      <div className="flex items-center gap-1.5 mb-2">
        {isDone && <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />}
        <h4 className={`text-sm font-medium truncate ${
          isDone ? 'line-through text-muted-foreground' : 'text-foreground'
        }`}>
          {item.title}
        </h4>
      </div>

      {/* Badges + meta row */}
      <div className="flex items-center gap-2 flex-wrap">
        {typeBadge && (
          <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded-full ${typeBadge.bg} ${typeBadge.text}`}>
            {typeBadge.label}
          </span>
        )}
        {isAdmin && item.themes && item.themes.length > 0 && item.themes.map((t) => (
          <span key={t.id} className="text-[10px] text-primary truncate">
            #{t.name}
          </span>
        ))}

        <div className="flex items-center gap-2 ml-auto text-xs text-muted-foreground">
          <span className="flex items-center gap-0.5">
            <ChevronUp className="w-3 h-3" />
            {item.vote_count}
          </span>
          {item.comment_count > 0 && (
            <span className="flex items-center gap-0.5">
              <MessageCircle className="w-3 h-3" />
              {item.comment_count}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
